import { updateHistory } from '@/libraries/api/faces'
import { DetectionMerger } from '@/libraries/util/detectionMerger'

const MAX_HISTORIES_TO_CACHE = 10

export const facesModule = {
  namespaced: true,
  state: {
    /**
     * A map of profile history arrays
     */
    cachedHistories: {},
    /**
     * A list of history UUIDs, for better reactivity.
     */
    cachedHistoryUUIDs: [],
  },
  mutations: {
    /**
     * Remove a specific event from the cached history.
     * The `data` object has two keys: `frontendFaceUUID` describing the history to inspect and a `detection` object to remove.
     */
    removeEventFromCachedHistory (state, data) {
      if (data.frontendFaceUUID && state.cachedHistories[data.frontendFaceUUID]) {
        const index = state.cachedHistories[data.frontendFaceUUID].findIndex(detection => detection.detection_id === data.detectionId)
        if (index >= 0) {
          const detectionForRemoval = state.cachedHistories[data.frontendFaceUUID][index]
          if (detectionForRemoval.merged && detectionForRemoval.merged.length >= 1) {
            // merged detections
            const cache = [...state.cachedHistories[data.frontendFaceUUID]]
            const topmostDetection = detectionForRemoval.merged[0]
            detectionForRemoval.merged.splice(0, 1)
            topmostDetection.merged = detectionForRemoval.merged
            cache[index] = topmostDetection
            state.cachedHistories[data.frontendFaceUUID] = cache
          } else {
            // single detection
            state.cachedHistories[data.frontendFaceUUID].splice(index, 1)
          }
        }
        // remove the UUID from history so that history is loaded again after navigating away
        const profileUUIDIndex = state.cachedHistoryUUIDs.findIndex(uuid => uuid === data.frontendFaceUUID)
        state.cachedHistoryUUIDs.splice(profileUUIDIndex, 1)
        state.cachedHistoryUUIDs.push(data.frontendFaceUUID)
      }
    },
    /**
     * Caches a history for a given uuid.
     * The `data` object has two keys: `frontendFaceUUID` describing the history to inspect and a `detection` object to remove.
     **/
    addHistoryToCache (state, data) {
      state.cachedHistories[data.frontendFaceUUID] = data.detections
      state.cachedHistoryUUIDs.push(data.frontendFaceUUID)
    },
    /* Removes the oldest cached history. */
    removeOldestHistoryFromCache (state) {
      const uuidOfOldestCache = state.cachedHistoryUUIDs[0]
      state.cachedHistories[uuidOfOldestCache] = []
      state.cachedHistoryUUIDs.splice(0, 1)
    },
  },
  getters: {
    /** Returns the profile that the user is viewing, based on the route. */
    currentProfileUUID (state, getters, rootState) {
      return rootState.route.params.id
    },
    /** The entire history - including events that may be hidden. */
    entireHistory (state, getters) {
      const uuid = getters.currentProfileUUID
      const hasHistory = state.cachedHistoryUUIDs.includes(uuid)
      // NOTE: This check is explicit, so VueJS will watch `hasHistory`.
      //       Without it, deep watching won't catch history changes and
      //       the UI won't react to the addition of a history.
      //       Don't combine this with the final return statement.
      if (!hasHistory) {
        return []
      }
      return state.cachedHistories[uuid]
    },
    /** Determines if the expanded or collapsed history is visible. */
    /** A getter exposing the number of the unfiltered history length. */
    entireHistoryLength (state, getters) {
      return getters.entireHistory.length
    },
  },
  actions: {
    /** Updates the history from the API, consolidates events, then updates the store. */
    fetchUpdatedHistory ({ state, commit, getters }) {
      const frontendFaceUUID = getters.currentProfileUUID
      if (!frontendFaceUUID) {
        console.debug(`No frontend_face_uuid. Skipping history update.`)
        return
      }

      commit('incrementRequestCountForServiceNamed', 'history', { root: true })
      updateHistory(frontendFaceUUID)
        .then(res => {
          const results = res.data.faces_matched
          const merger = new DetectionMerger()
          if (!merger) {
            console.debug('DetectionMerger not detected. Cannot merge detections.')
          }

          const detections = merger ? merger.groupedDetectionsFromDetections(results) : results
          const payload = { frontendFaceUUID, detections }

          if (state.cachedHistoryUUIDs.length >= MAX_HISTORIES_TO_CACHE) {
            commit('removeOldestHistoryFromCache')
          }

          commit('addHistoryToCache', payload)
        })
        .catch(error => {
          console.error('Error loading histroy: ', error)
        })
        .then(
          function () {
            commit('decrementRequestCountForServiceNamed', 'history', {
              root: true,
            })
          }
        )
    },
  },
}
